<script setup lang="ts" generic="TData, TValue">
import { ColumnDef, SortingState, PaginationState, Row, FlexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useVueTable } from '@tanstack/vue-table';
import { ArrowUp, ArrowDown, ChevronsUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-vue-next';
import { computed, watch, ref, useSlots, type HTMLAttributes } from 'vue';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import FadeTransition from '@/components/transitions/FadeTransition.vue';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import Spinner from '@/components/ui/Spinner.vue';
import DataTableFacetFilter from './DataTableFacetFilter.vue';
import type { FacetOption, FacetSelection } from './types';

const props = withDefaults(
    defineProps<{
        columns: ColumnDef<TData, TValue>[];
        data: TData[];
        totalRows?: number;
        loading?: boolean;
        serverSide?: boolean;
        pageSize: number;
        pageSizeOptions: number[];
        // Stacking class for the page-size menu, for a table drawn above the default layer (a full-screen overlay). Optional; none when unset.
        pageSizeMenuClass?: string;
        page: number;
        totalPages?: number;
        selectable?: boolean;
        // eslint-disable-next-line
        rowId?: (row: TData) => string;
        // eslint-disable-next-line
        rowClass?: (row: TData) => string;
        // A label to draw as a full-width divider row above a row, or null for none. Optional; no dividers when unset.
        // eslint-disable-next-line
        rowDivider?: (row: TData, index: number) => string | null;
        // eslint-disable-next-line
        globalFilterFunction?: (row: Row<TData>, columnId: string, filterValue: string) => boolean;
        sorting?: SortingState;
        rowSelection?: Record<string, boolean>;
        filter?: string;
        enablePagination?: boolean;
        // When true, a click anywhere on a row (that doesn't stop propagation itself, e.g. a
        // selection checkbox) emits 'row-click' with that row's data.
        rowClickable?: boolean;
        // Which facet values are ticked, by column id - see FacetedDataTable/DataTableFacetFilter.
        // The table renders and reports the selection but does not apply it: the parent's data is
        // expected to already reflect it.
        columnFilters?: FacetSelection;
        // Column id to the multiselect offered in that column's header.
        facets?: Record<string, { title: string; options: FacetOption[] }>;
        // Tailwind max-height class capping the scroll container (e.g. max-h-[60vh]), so the header
        // stays put via sticky while the rows scroll past it instead of growing the page.
        maxBodyHeight?: string;
        // Classes for the <table> element itself - e.g. table-fixed so column widths (set per
        // column via meta.headerClass/meta.cellClass) hold regardless of cell content, which
        // several independently-mounted instances of the same columns need to stay visually aligned.
        tableClass?: HTMLAttributes['class'];
        // Set when several DataTable instances share one rowSelection object across their own,
        // disjoint data sets (e.g. one table per expanded Section, selection shared at Barn scope
        // for a cross-Section bulk action) - see the rows watcher below. Leaves a selected id alone
        // the moment it's foreign to this instance (never one of its own rows to begin with) instead
        // of reading that as staleness and clearing the whole shared selection.
        preserveForeignSelection?: boolean;
    }>(),
    {
        loading: false,
        pageSizeMenuClass: undefined,
        totalRows: undefined,
        serverSide: false,
        selectable: false,
        totalPages: undefined,
        rowClass: undefined,
        rowDivider: undefined,
        sorting: undefined,
        rowSelection: undefined,
        filter: undefined,
        enablePagination: true,
        rowClickable: false,
        columnFilters: undefined,
        facets: undefined,
        maxBodyHeight: undefined,
        tableClass: undefined,
        preserveForeignSelection: false,
    }
);
const emit = defineEmits<{
    'update:filter': [value: string];
    'update:sorting': [value: SortingState];
    'update:pageSize': [value: number];
    'update:page': [value: number];
    'update:selectedRows': [value: string[]];
    'update:rowSelection': [value: Record<string, boolean>];
    'update:columnFilters': [value: FacetSelection];
    'row-click': [row: TData];
}>();

const internalSorting = ref<SortingState>([]);
const internalRowSelection = ref<Record<string, boolean>>({});
const internalColumnFilters = ref<FacetSelection>({});

const columnFilters = computed({
    get: () => props.columnFilters ?? internalColumnFilters.value,
    set: (value) => {
        if (props.columnFilters !== undefined) {
            emit('update:columnFilters', value);
        } else {
            internalColumnFilters.value = value;
        }
    },
});

function facetFor(columnId: string) {
    return props.facets?.[columnId];
}

function facetValue(columnId: string): (string | number)[] {
    return columnFilters.value[columnId] ?? [];
}

function setFacetValue(columnId: string, value: (string | number)[]) {
    const next = { ...columnFilters.value };

    if (value.length) {
        next[columnId] = value;
    } else {
        delete next[columnId];
    }

    columnFilters.value = next;
}

const pagination = computed<PaginationState>({
    get: () => ({
        pageSize: props.pageSize,
        pageIndex: props.page - 1,
    }),
    set: ({ pageSize, pageIndex }: PaginationState) => {
        emit('update:pageSize', pageSize);
        emit('update:page', pageIndex + 1);
    },
});
const sorting = computed({
    get: () => props.sorting ?? internalSorting.value,
    set: (value) => {
        if (props.sorting !== undefined) {
            emit('update:sorting', value);
        } else {
            internalSorting.value = value;
        }
    },
});
const rowSelection = computed({
    get: () => props.rowSelection ?? internalRowSelection.value,
    set: (value) => {
        emit('update:rowSelection', value);

        if (props.rowSelection === undefined) {
            internalRowSelection.value = value;
        }
    },
});
const filter = computed({
    get: () => props.filter,
    set: (value) => emit('update:filter', value || ''),
});

const columns = computed(() =>
    props.columns.map((column) => ({
        ...column,
        enableGlobalFilter: props.globalFilterFunction ? true : column.enableGlobalFilter ?? false,
        enableSorting: column.enableSorting ?? false,
    }))
);

const table = useVueTable({
    initialState: {
        sorting: sorting.value,
        globalFilter: filter.value,
        pagination: pagination.value,
    },
    get data() {
        return props.data;
    },
    get columns() {
        return columns.value;
    },
    get pageCount() {
        return props.serverSide ? props.totalPages : undefined;
    },
    get rowCount() {
        return props.serverSide ? props.totalRows : undefined;
    },
    state: {
        get sorting() {
            return sorting.value;
        },
        get pagination() {
            return pagination.value;
        },
        get rowSelection() {
            return rowSelection.value;
        },
        get globalFilter() {
            return filter.value;
        },
    },
    onSortingChange: (updater) => {
        sorting.value = typeof updater === 'function' ? updater(sorting.value) : updater;
    },
    onPaginationChange: (updater) => {
        pagination.value = typeof updater === 'function' ? updater(pagination.value) : updater;

        // Guarded rather than an unconditional clear: when `rowSelection` is a ref this component's
        // parent shares across several simultaneously-mounted tables (e.g. one DataTable per expanded
        // Section on Groups overview), writing a fresh `{}` here even though it was already empty
        // still swaps the shared ref's identity - which re-triggers every other table sharing it, each
        // of which can itself re-enter this same path (see the pageIndex guard below), compounding into
        // a real infinite update loop rather than just wasted work.
        if (Object.keys(rowSelection.value).length > 0) {
            rowSelection.value = {};
        }
    },
    onRowSelectionChange: (updater) => {
        rowSelection.value = typeof updater === 'function' ? updater(rowSelection.value) : updater;
    },
    onGlobalFilterChange: (updater) => {
        filter.value = typeof updater === 'function' ? updater(filter.value) : updater;
    },
    globalFilterFn: props.globalFilterFunction,
    manualSorting: props.serverSide,
    manualPagination: props.serverSide,
    manualFiltering: props.serverSide,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: props.rowId ?? ((row: any) => row.id?.toString() ?? ''),
    autoResetPageIndex: false,
});

function getAlignClass(align: string | undefined): string {
    switch (align) {
        case 'left':
            return 'text-left';
        case 'right':
            return 'text-right';
        case 'center':
            return 'text-center';
        default:
            return 'text-left';
    }
}

/** The accessorKey a header/item slot is named by; undefined for a display or group column. Kept out of the template because vue-tsc cannot parse a type cast inside a template literal. */
function accessorKeyOf(column: { columnDef: ColumnDef<TData, TValue> }): string | undefined {
    return (column.columnDef as { accessorKey?: string }).accessorKey;
}

/** The slot a column's totals cell renders from, keyed by its accessorKey like the header and item slots, falling back to the column id. */
function totalsSlotName(column: { id: string; columnDef: ColumnDef<TData, TValue> }): string {
    return `totals[${accessorKeyOf(column) ?? column.id}]`;
}

const slots = useSlots();
/** A totals row is drawn under the column names when any visible column offers a totals cell; none otherwise. */
const hasTotals = computed(() => table.getVisibleLeafColumns().some((column) => slots[totalsSlotName(column)] !== undefined));

const rows = computed(() => (props.serverSide ? table.getFilteredRowModel().rows : table.getRowModel().rows) || []);
const rowsCount = computed(() => props.totalRows ?? table.getFilteredRowModel().rows.length);

const start = computed(() => {
    const pageIndex = table.getState().pagination.pageIndex;
    const pageSize = table.getState().pagination.pageSize;

    return pageIndex * pageSize + 1;
});
const end = computed(() => {
    const itemEnd = (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize;

    return rowsCount.value > itemEnd ? itemEnd : rowsCount.value;
});

// Previous call's own row ids - lets the `preserveForeignSelection` branch below tell "this id used
// to be one of my rows and just disappeared" (prune it) apart from "this id was never one of my
// rows" (a sibling table's selection, passing through the shared object; leave it alone). Starts
// empty, so nothing already selected before this table's first mount is ever read as having
// "disappeared" from it.
let previousRowIds = new Set<string>();

watch(
    () => rows.value,
    (newValue) => {
        const newRowIds = new Set(newValue.map((r) => r.id));

        if (props.preserveForeignSelection) {
            const next = Object.fromEntries(Object.entries(rowSelection.value).filter(([id]) => newRowIds.has(id) || !previousRowIds.has(id)));
            if (Object.keys(next).length !== Object.keys(rowSelection.value).length) {
                rowSelection.value = next;
            }
        } else if (Object.keys(rowSelection.value).every((k) => newRowIds.has(k)) === false) {
            rowSelection.value = {};
        }
        previousRowIds = newRowIds;

        if (props.loading) {
            return;
        }

        const pageCount = props.totalPages ?? table.getPageCount();
        const pageIndex = table.getState().pagination.pageIndex;

        // Set the pagination to the first page if the data changes and the current page is out of
        // bounds - guarded by `pageIndex !== 0` since a table with zero rows (e.g. a search that
        // leaves one Section's table with no matches) makes `pageCount` 0, which makes this condition
        // true even while already sitting on page 0; calling `setPageIndex(0)` there is a true no-op
        // but still fires `onPaginationChange` (see its own guard above) - without this, re-running it
        // on an already-empty table loops this watcher against itself indefinitely.
        if (pageIndex > pageCount - 1 && pageIndex !== 0) {
            table.setPageIndex(0);
            table.initialState.pagination.pageIndex = 0;
        }
    },
    { immediate: true }
);

watch(
    () => filter.value,
    () => {
        table.setPageIndex(0);
    }
);
</script>

<template>
    <div class="relative">
        <Table :class="tableClass" :container-class="maxBodyHeight ? ['overflow-y-auto', maxBodyHeight] : undefined">
            <TableHeader>
                <TableRow v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id" class="hover:bg-inherit">
                    <TableHead
                        v-for="header in headerGroup.headers"
                        :key="header.id"
                        :class="[
                            getAlignClass(header.column.columnDef.meta?.align),
                            header.column.columnDef.meta?.headerClass || '',
                            maxBodyHeight ? 'sticky top-0 z-20 bg-white' : '',
                        ]"
                    >
                        <!-- Only an accessor column reaches a named `header[...]`/`item[...]` slot - a display or group
                             column's definition carries no accessorKey and renders through FlexRender below instead. -->
                        <slot :name="`header[${accessorKeyOf(header.column)}]`">
                            <div class="flex items-center" :class="{ 'justify-end': header.column.columnDef.meta?.align === 'right', 'justify-center': header.column.columnDef.meta?.align === 'center' }">
                            <DropdownMenu v-if="header.column.getCanSort() && !header.isPlaceholder" :modal="false">
                                <DropdownMenuTrigger as-child>
                                    <Button
                                        v-if="header.column.getCanSort() && !header.isPlaceholder"
                                        :aria-label="
                                            header.column.getIsSorted()
                                                ? header.column.getIsSorted() === 'asc'
                                                    ? $t('global.uiElements.dataTable.buttons.sortedAscendingClickToSort')
                                                    : $t('global.uiElements.dataTable.buttons.sortedDescendingClickToSort')
                                                : $t('global.uiElements.dataTable.buttons.clickToSort')
                                        "
                                        variant="ghost"
                                        class="px-3 -ml-3 gap-x-1.5 data-[state=open]:bg-gray-100"
                                        :class="{ '-ml-3': header.column.columnDef.meta?.align === 'left', 'ml-3': header.column.columnDef.meta?.align === 'right' }"
                                    >
                                        <FlexRender :render="header.column.columnDef.header" :props="header.getContext()" />
                                        <template v-if="header.column.getCanSort()">
                                            <FadeTransition mode="out-in">
                                                <ArrowUp v-if="header.column.getIsSorted() && sorting?.[0]?.desc === false" class="h-4 w-4 -mr-0.5" aria-hidden="true" />
                                                <ArrowDown v-else-if="header.column.getIsSorted()" class="h-4 w-4 -mr-0.5" aria-hidden="true" />
                                                <ChevronsUpDown v-else class="h-4 w-4 transition-transform -mr-0.5" aria-hidden="true" />
                                            </FadeTransition>
                                        </template>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" class="space-y-0.5">
                                    <DropdownMenuItem
                                        :aria-label="$t('global.uiElements.dataTable.buttons.sortAscending')"
                                        :class="{ 'bg-gray-100': header.column.getIsSorted() === 'asc' }"
                                        @select="header.column.toggleSorting(false)"
                                    >
                                        <ArrowUp class="mr-2 h-4 w-4" />
                                        <span>{{ $t('global.uiElements.dataTable.buttons.ascending') }}</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        :aria-label="$t('global.uiElements.dataTable.buttons.sortDescending')"
                                        :class="{ 'bg-gray-100': header.column.getIsSorted() === 'desc' }"
                                        @select="header.column.toggleSorting(true)"
                                    >
                                        <ArrowDown class="mr-2 h-4 w-4" />
                                        <span>{{ $t('global.uiElements.dataTable.buttons.descending') }}</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <FlexRender v-else-if="!header.isPlaceholder" :render="header.column.columnDef.header" :props="header.getContext()" />
                            <DataTableFacetFilter
                                v-if="facetFor(header.column.id)"
                                :title="facetFor(header.column.id)?.title ?? ''"
                                :options="facetFor(header.column.id)?.options ?? []"
                                :model-value="facetValue(header.column.id)"
                                @update:model-value="setFacetValue(header.column.id, $event)"
                            />
                            </div>
                        </slot>
                    </TableHead>
                </TableRow>
            </TableHeader>
            <TableBody class="relative">
                <!-- Totals read as a second heading rather than a footer, beside the column names they belong to, as on the records tables. -->
                <TableRow v-if="hasTotals" class="hover:bg-inherit">
                    <TableCell v-for="column in table.getVisibleLeafColumns()" :key="column.id" :class="[getAlignClass(column.columnDef.meta?.align), 'bg-gray-50 font-semibold text-gray-900']">
                        <slot :name="totalsSlotName(column)" />
                    </TableCell>
                </TableRow>
                <template v-if="rows.length">
                    <template v-for="(row, index) in rows" :key="row.id">
                        <tr v-if="rowDivider?.(row.original as TData, index)" class="border-b border-gray-200 bg-gray-50">
                            <td :colspan="row.getVisibleCells().length" class="px-4 py-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                                {{ rowDivider(row.original as TData, index) }}
                            </td>
                        </tr>
                        <TableRow
                            :data-state="row.getIsSelected() ? 'selected' : undefined"
                            :class="[rowClass?.(row.original as TData), { 'cursor-pointer': rowClickable }]"
                            @click="rowClickable && emit('row-click', row.original as TData)"
                        >
                            <TableCell
                                v-for="cell in row.getVisibleCells()"
                                :key="cell.id"
                                :class="[getAlignClass(cell.column.columnDef.meta?.align), cell.column.columnDef.meta?.cellClass || '']"
                            >
                                <slot :name="`item[${accessorKeyOf(cell.column)}]`" :item="row.original" :value="cell.getValue()">
                                    <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
                                </slot>
                            </TableCell>
                        </TableRow>
                    </template>
                </template>
                <tr v-else>
                    <td :colspan="table.getFlatHeaders().length" class="h-24 text-center text-gray-500">
                        {{ loading ? '' : $t('global.uiElements.dataTable.texts.noResults') }}
                    </td>
                </tr>
            </TableBody>
        </Table>
        <div v-if="enablePagination" class="flex w-full flex-col-reverse items-center justify-between gap-4 overflow-auto sm:flex-row sm:gap-8 px-4 py-3 border-t border-gray-200">
            <div class="flex-1 whitespace-nowrap text-normal text-gray-500">
                {{
                    selectable && Object.keys(rowSelection).length
                        ? $t('global.uiElements.dataTable.texts.selectedRows')
                              .replace('%SELECTED%', table.getFilteredSelectedRowModel().rows.length.toString())
                              .replace('%TOTAL%', table.getFilteredRowModel().rows.length.toString())
                        : $t('global.uiElements.dataTable.texts.showingRange')
                              .replace('%START%', rows.length ? start.toString() : '0')
                              .replace('%END%', end.toString())
                              .replace('%TOTAL%', rowsCount.toString())
                }}
            </div>
            <div class="flex items-center space-x-2">
                <div class="whitespace-nowrap text-normal font-medium">{{ $t('global.uiElements.dataTable.texts.pageSize') }}</div>
                <Select :model-value="String(table.getState().pagination.pageSize)" @update:model-value="table.setPageSize(Number($event))">
                    <SelectTrigger class="w-24">
                        <SelectValue :placeholder="String(table.getState().pagination.pageSize)" />
                        <template #icon>
                            <ChevronsUpDown class="size-4 opacity-50 flex-shrink-0" aria-hidden="true" />
                        </template>
                    </SelectTrigger>
                    <SelectContent side="top" :class="pageSizeMenuClass">
                        <SelectItem v-for="option in pageSizeOptions || [10, 20, 30, 40, 50]" :key="option" :value="String(option)" class="text-normal">{{ option }}</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div class="flex items-center gap-x-4">
                <div className="flex items-center justify-center text-normal font-medium">
                    {{
                        $t('global.uiElements.dataTable.texts.page')
                            .replace('%CURRENT_PAGE%', (table.getState().pagination.pageIndex + 1).toString())
                            .replace('%TOTAL%', (table.getPageCount() || 1).toString())
                    }}
                </div>
                <div class="flex items-center space-x-1">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button variant="outline" size="icon" :disabled="!table.getCanPreviousPage() || loading" @click="table.setPageIndex(0)">
                                    <span class="sr-only">{{ $t('global.uiElements.dataTable.buttons.firstPage') }}</span>
                                    <ChevronsLeft class="size-5" aria-hidden="true" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{{ $t('global.uiElements.dataTable.buttons.firstPage') }}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button variant="outline" size="icon" :disabled="!table.getCanPreviousPage() || loading" @click="table.previousPage()">
                                    <span class="sr-only">{{ $t('global.uiElements.dataTable.buttons.previousPage') }}</span>
                                    <ChevronLeft class="size-5" aria-hidden="true" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{{ $t('global.uiElements.dataTable.buttons.previousPage') }}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button variant="outline" size="icon" :disabled="!table.getCanNextPage() || loading" @click="table.nextPage()">
                                    <span class="sr-only">{{ $t('global.uiElements.dataTable.buttons.nextPage') }}</span>
                                    <ChevronRight class="size-5" aria-hidden="true" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{{ $t('global.uiElements.dataTable.buttons.nextPage') }}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button variant="outline" size="icon" :disabled="!table.getCanNextPage() || loading" @click="table.setPageIndex(table.getPageCount() - 1)">
                                    <span class="sr-only">{{ $t('global.uiElements.dataTable.buttons.lastPage') }}</span>
                                    <ChevronsRight class="size-5" aria-hidden="true" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{{ $t('global.uiElements.dataTable.buttons.lastPage') }}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </div>
        </div>
        <Transition
            enter-active-class="duration-200 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="duration-100 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-white/80">
                <div class="flex justify-center items-center gap-2">
                    <div class="font-medium">{{ $t('global.uiElements.dataTable.texts.loading') }}</div>
                    <Spinner class="w-5 h-5 animate-spin" />
                </div>
            </div>
        </Transition>
    </div>
</template>
